import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

/**
 * Vorschlagssuche im Kopfbereich.
 *
 * Aufteilung: die Schale steht in `snippets/search-modal.liquid`, die
 * Trefferliste in `sections/predictive-search.liquid`, das Aussehen in
 * `assets/ol-suche.css`. Diese Datei kuemmert sich um das Nachladen und
 * um die Bedienung mit der Tastatur.
 *
 * Tastatur: Pfeil ab/auf laufen durch die Zeilen, die Eingabetaste
 * oeffnet die hervorgehobene, Escape leert das Fenster und setzt den
 * Schreibzeiger zurueck ins Eingabefeld. Der Schreibzeiger bleibt dabei
 * immer im Eingabefeld - die hervorgehobene Zeile wird ueber
 * `aria-activedescendant` bekanntgegeben, so wie es fuer ein Kombifeld
 * vorgesehen ist.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - Eingabefeld.
 * @property {HTMLElement} predictiveSearchResults - Scrollbereich mit den Treffern.
 * @property {HTMLElement} resetButton - Knopf zum Leeren.
 * @property {HTMLElement} [footer] - Streifen mit "Alle anzeigen".
 * @property {HTMLElement} [loadingState] - Platzhalter waehrend des Ladens.
 * @property {HTMLElement[]} [resultsItems] - Trefferzeilen.
 * @property {HTMLElement} [recentlyViewedWrapper] - Block "Zuletzt angesehen".
 * @property {HTMLElement[]} [recentlyViewedTitle] - Ueberschrift dieses Blocks.
 * @property {HTMLElement[]} [recentlyViewedItems] - Zeilen dieses Blocks.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    this.#syncPanelState();

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Klicks ins Leere sollen den Schreibzeiger nicht aus dem Eingabefeld werfen.
   * @param {MouseEvent} event - Das Klickereignis.
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  /**
   * Gleicher Zweck, aber ueber `on:click` aus der Vorlage aufgerufen.
   * @param {MouseEvent} event - Das Klickereignis.
   */
  handleModalClick(event) {
    this.#handleModalClick(event);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  /**
   * Alle anwaehlbaren Zeilen in der Reihenfolge, in der sie im Fenster
   * stehen. `data-such-treffer` steht an jeder Zeile, die die Vorlagen
   * ausgeben; die zweite Abfrage ist nur ein Netz fuer aeltere Markup-
   * Reste.
   * @returns {HTMLElement[]} Die Zeilen.
   */
  get #allResultsItems() {
    const items = Array.from(this.querySelectorAll('[data-such-treffer]'));

    if (items.length > 0) {
      return /** @type {HTMLElement[]} */ (items.filter((item) => item instanceof HTMLElement));
    }

    const fallback = Array.from(
      this.querySelectorAll('[ref="resultsItems[]"], [ref="recentlyViewedItems[]"], .predictive-search-results__card')
    );

    return /** @type {HTMLElement[]} */ (fallback.filter((item) => item instanceof HTMLElement));
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    const items = this.#allResultsItems;
    const input = this.refs.searchInput;

    if (!items.length) {
      input?.removeAttribute('aria-activedescendant');
      return;
    }

    let activeItem = null;

    for (const item of items) {
      item.classList.remove('keyboard-focus');
    }

    for (const [itemIndex, item] of items.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.setAttribute('aria-selected', 'false');
      }
    }

    // Der Schreibzeiger bleibt im Eingabefeld; welche Zeile gemeint ist,
    // sagt `aria-activedescendant`. Ohne die id waere die Hervorhebung
    // fuer Vorlese-Programme unsichtbar.
    if (activeItem?.id) {
      input?.setAttribute('aria-activedescendant', activeItem.id);
    } else {
      input?.removeAttribute('aria-activedescendant');
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    input?.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Haelt `aria-expanded`, den Ladezustand und den Streifen "Alle
   * anzeigen" mit dem Inhalt des Fensters im Gleichklang.
   *
   * Bewusst in JavaScript und nicht in CSS: die frueheren Regeln liefen
   * ueber `:has()`, und darauf darf in diesem Projekt kein Aufbau ruhen.
   */
  #syncPanelState() {
    const { searchInput, footer } = this.refs;
    const hatTreffer = this.#allResultsItems.length > 0;
    const hatMeldung = Boolean(this.querySelector('.predictive-search-results__no-results, .ol-sr__leer'));

    searchInput?.setAttribute('aria-expanded', hatTreffer || hatMeldung ? 'true' : 'false');

    if (footer) {
      footer.hidden = !hatTreffer;
    }
  }

  /**
   * Zeigt oder versteckt die hellen Platzhalter.
   * @param {boolean} an - Sichtbar oder nicht.
   */
  #setLoading(an) {
    const { loadingState } = this.refs;
    if (loadingState) loadingState.hidden = !an;
  }

  /**
   * Pfeiltasten, Eingabetaste und Escape.
   * @param {KeyboardEvent} event - Das Tastenereignis.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      // Steht etwas im Feld, raeumt Escape zuerst nur das Fenster auf und
      // laesst den Dialog stehen - der Schreibzeiger kehrt ins Eingabefeld
      // zurueck. Ist das Feld schon leer, darf das Ereignis weiter nach
      // oben laufen und der Dialog schliesst wie gewohnt.
      if (this.refs.searchInput.value.length > 0) {
        event.preventDefault();
        event.stopPropagation();
        this.#resetSearch();
        this.refs.searchInput.focus();
      }
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (
          this.#currentIndex < 0 &&
          singleResultContainer instanceof HTMLElement &&
          singleResultContainer.dataset.singleResultUrl
        ) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
        this.#syncPanelState();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');
    // Ohne diesen Parameter liefert die Vorschlagssuche nur Produkte - das
    // ist Shopifys Vorgabe, sobald `resources[type]` fehlt. Der Abschnitt
    // `sections/predictive-search.liquid` stellt Beitraege und Seiten
    // laengst dar; es fehlte allein die Anforderung. Wer "Windows 11
    // aktivieren" tippt, sucht die Anleitung und nicht eine Lizenz.
    // `resources[limit]` bleibt absichtlich unberuehrt, damit sich die
    // Zahl der Produktvorschlaege nicht nebenbei aendert.
    url.searchParams.set('resources[type]', 'product,article,page');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    const deferredPromise = SearchUpdateEvent.createPromise();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query: searchTerm,
        },
        promise: deferredPromise.promise,
      })
    );

    this.#setLoading(true);

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) {
          this.#setLoading(false);
          deferredPromise.resolve({ totalCount: 0 });
          return;
        }

        if (abortController.signal.aborted) {
          deferredPromise.reject(new Error('Fetch aborted by user'));
          return;
        }

        morph(predictiveSearchResults, resultsMarkup);

        this.#setLoading(false);
        this.#resetScrollPositions();
        this.#syncPanelState();

        const resultCount = this.#allResultsItems.length;
        deferredPromise.resolve({ totalCount: resultCount });
      })
      .catch((error) => {
        this.#setLoading(false);
        deferredPromise.reject(error);
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    searchInput.removeAttribute('aria-activedescendant');
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#setLoading(false);
    this.#resetScrollPositions();
    this.#syncPanelState();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
